import { useState } from 'react';

import { openEmbeddedSignup } from './meta-embedded-signup';
import { describeAttempt } from './onboarding-diagnosis';
import {
  useCancelAttempt,
  usePostPopupResult,
  useResumeAttempt,
  useStartAttempt,
} from './use-onboarding';

import type { Acao, Tone } from './onboarding-diagnosis';
import type { OnboardingAttemptWire } from './types';
import type { ReactElement } from 'react';

const PILULA: Record<Tone, string> = {
  ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  bad: 'bg-red-50 border-red-200 text-red-700',
  warn: 'bg-amber-50 border-amber-200 text-amber-700',
  mute: 'bg-bg-muted border-border-default text-text-secondary',
  busy: 'bg-amber-100 border-amber-200 text-amber-900',
};

interface PopupCfg {
  appId: string;
  configId: string | null;
  graphVersion: string;
  featureType: string | null;
}

/** Negrito de `**assim**` sem trazer um parser de Markdown para uma frase. */
function comDestaque(frase: string): ReactElement {
  return (
    <>
      {frase
        .split('**')
        .map((p, i) => (i % 2 === 1 ? <strong key={p + String(i)}>{p}</strong> : p))}
    </>
  );
}

const RETENCOES = new Set([6, 12, 24, 60]);

/** A escolha do histórico feita no modal viaja junto quando a tentativa é refeita (W3 lê). */
export function historyDe(
  a: OnboardingAttemptWire,
): { import: boolean; retentionMonths: 6 | 12 | 24 | 60 } | undefined {
  const h = a.options?.history;
  if (h === undefined || a.path !== 'business_app' || !RETENCOES.has(h.retentionMonths)) {
    return undefined;
  }
  return { import: h.import, retentionMonths: h.retentionMonths as 6 | 12 | 24 | 60 };
}

export interface AttemptActionsProps {
  attempt: OnboardingAttemptWire;
  canManage: boolean;
  /** «Escolher outro caminho» (§7 #4): cancela a tentativa e quem chama abre o modal de novo. */
  onEscolherOutroCaminho?: () => void;
}

/**
 * A frase da tentativa + o botão do gesto + o formulário do PIN + os erros de mutação — num só
 * lugar, usado pelo `WhatsAppConnectCard` (antes do canal existir) **e** pelo `ChannelCard`
 * (o canal existe desde o passo 1). Sem isto, o «Informar o PIN» do 133005 só existia no
 * cartão que a aba já tinha escondido (achado do revisor, 30/Set).
 *
 * Toda mutação renderiza o próprio erro: um 409 ou rede fora nunca deixa o tenant clicando no
 * vazio.
 */
export function AttemptActions({
  attempt,
  canManage,
  onEscolherOutroCaminho,
}: AttemptActionsProps): ReactElement | null {
  const [pedindoPin, setPedindoPin] = useState(false);
  const [pin, setPin] = useState('');
  const [erroLocal, setErroLocal] = useState<string | null>(null);
  const start = useStartAttempt();
  const postar = usePostPopupResult();
  const retomar = useResumeAttempt();
  const cancelar = useCancelAttempt();

  const d = describeAttempt(attempt);
  const acao = d.action;
  const popupAberto = attempt.status === 'opened' && attempt.metaEvent === null;

  async function abrirPopup(id: string, popup: PopupCfg): Promise<void> {
    if (popup.configId === null) {
      setErroLocal(
        'A Lume ainda não terminou a configuração na Meta (falta o identificador da tela de conexão).',
      );
      return;
    }
    try {
      const r = await openEmbeddedSignup({
        appId: popup.appId,
        configId: popup.configId,
        graphVersion: popup.graphVersion,
        featureType: popup.featureType,
      });
      postar.mutate({ id, body: r });
    } catch (e) {
      setErroLocal(e instanceof Error ? e.message : 'não foi possível abrir a janela da Meta');
    }
  }

  /** Retomar/reabrir é reabrir o popup: o Engine devolve a aberta ou cria uma nova com a mesma escolha. */
  function reabrir(): void {
    setErroLocal(null);
    const history = historyDe(attempt);
    start.mutate(
      { path: attempt.path, ...(history === undefined ? {} : { history }) },
      {
        onSuccess: (s) => {
          void abrirPopup(s.attempt.id, s.popup);
        },
      },
    );
  }

  function agir(a: Acao): void {
    switch (a.kind) {
      case 'retomar':
      case 'reabrir':
        reabrir();
        return;
      case 'tentar-de-novo':
        retomar.mutate({ id: attempt.id });
        return;
      case 'informar-pin':
        setPedindoPin(true);
        return;
      case 'outro-caminho':
        cancelar.mutate(attempt.id, { onSuccess: () => onEscolherOutroCaminho?.() });
        return;
      default:
        return;
    }
  }

  const erros = [
    erroLocal,
    start.isError ? start.error.message : null,
    postar.isError ? postar.error.message : null,
    retomar.isError ? retomar.error.message : null,
    cancelar.isError ? cancelar.error.message : null,
  ].filter((e): e is string => e !== null);

  if (d.sentence === null && erros.length === 0 && !popupAberto) return null;

  return (
    <>
      {d.sentence !== null && (
        <div
          className={`mt-3 flex gap-2 rounded-md border px-3 py-2.5 text-xs leading-relaxed ${PILULA[d.tone]}`}
          role={d.tone === 'bad' ? 'alert' : undefined}
        >
          <span className="font-bold">
            {d.tone === 'bad' ? '✗' : d.tone === 'busy' ? '…' : '!'}
          </span>
          <div className="min-w-0 flex-1">{comDestaque(d.sentence)}</div>
          {canManage && acao !== null && acao.href === undefined && (
            <button
              type="button"
              className="self-center whitespace-nowrap rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
              disabled={start.isPending || retomar.isPending || cancelar.isPending}
              onClick={() => {
                agir(acao);
              }}
            >
              {acao.label}
            </button>
          )}
          {acao?.href !== undefined && (
            <a
              className="self-center whitespace-nowrap rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary"
              href={acao.href}
              target="_blank"
              rel="noreferrer"
            >
              {acao.label}
            </a>
          )}
        </div>
      )}

      {popupAberto && canManage && (
        <p className="mt-2 text-xs text-text-secondary">
          Não vê a janela? Ela pode ter aberto atrás desta, ou o navegador pode ter bloqueado.{' '}
          <button type="button" className="text-accent underline" onClick={reabrir}>
            Abrir de novo
          </button>
          .
        </p>
      )}

      {pedindoPin && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            retomar.mutate(
              { id: attempt.id, pin },
              {
                onSuccess: () => {
                  setPedindoPin(false);
                  setPin('');
                },
              },
            );
          }}
        >
          <label className="text-xs text-text-secondary" htmlFor={`pin-${attempt.id}`}>
            PIN de 6 dígitos
            <input
              id={`pin-${attempt.id}`}
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              value={pin}
              onChange={(e) => {
                setPin(e.target.value.replace(/\D/g, ''));
              }}
              className="mt-1 block w-32 rounded-md border border-border-strong bg-surface px-2 py-1.5 font-mono text-xs text-text-primary"
            />
          </label>
          <button
            type="submit"
            disabled={pin.length !== 6 || retomar.isPending}
            className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            Continuar
          </button>
          <button
            type="button"
            className="rounded-md border border-border-strong bg-surface px-3 py-1.5 text-xs text-text-secondary"
            onClick={() => {
              setPedindoPin(false);
            }}
          >
            Cancelar
          </button>
        </form>
      )}

      {erros.map((e) => (
        <p key={e} className="mt-2 text-xs text-red-700" role="alert">
          Não deu: {e}
        </p>
      ))}
    </>
  );
}
